import React, { useState } from 'react';
import { Download, Scissors, Settings, AlertTriangle } from 'lucide-react';

const SegmentList = ({ 
  aspectRatio, 
  setAspectRatio, 
  timeSegments, 
  onProcessVideo,
  isProcessing,
  downloadUrl
}) => {
  const [transitionType, setTransitionType] = useState('fade');
  const [quality, setQuality] = useState('high');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingStage, setProcessingStage] = useState('');
  const [processingError, setProcessingError] = useState(null);
  const [downloadStarted, setDownloadStarted] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  
  // Map processing stages to user-friendly descriptions
  const stageDescriptions = {
    initializing: 'Preparing to process your video',
    downloading: 'Downloading YouTube video',
    processing_segments: 'Cutting selected segments',
    combining_segments: 'Combining video segments',
    converting_aspect_ratio: 'Adjusting to selected aspect ratio',
    finalizing: 'Finalizing your video',
    completed: 'Processing complete!',
    error: 'Error occurred during processing'
  };

  const handleProcessVideo = () => {
    // Reset progress display
    setProcessingProgress(0);
    setProcessingStage('initializing');
    setProcessingError(null);
    
    onProcessVideo({
      aspectRatio,
      transitionType,
      quality,
      onProgressUpdate: (progress, stage, error) => {
        // Update our local state with progress information
        setProcessingProgress(progress);
        setProcessingStage(stage);
        
        // Check for errors (-1 progress indicates an error)
        if (progress === -1 && stage === 'error') {
          setProcessingError(error || 'Error during processing. Please try again.');
        }
        
        // When progress is complete
        if (progress === 100 && stage === 'completed') {
          console.log('Processing completed successfully!');
        }
      }
    });
  };

  const aspectRatioOptions = [
    { value: '9:16', label: '9:16 (Instagram Reels/TikTok)', icon: '📱' },
    { value: '16:9', label: '16:9 (YouTube/Landscape)', icon: '🖥️' },
    { value: '4:5', label: '4:5 (Instagram Posts)', icon: '📷' },
    { value: '1:1', label: '1:1 (Square)', icon: '⬛' }
  ];
  
  // Handle download for both development and production environments
  const handleDownload = (e) => {
    e.preventDefault();
    
    // In development mode, show message and return
    if (process.env.NODE_ENV === 'development') {
      alert('In development mode, actual video downloads are not available.\n\nIn production, this would download your processed video file.');
      return;
    }
    
    // In production, handle the download properly
    if (downloadStarted) {
      return; // Prevent multiple clicks
    }
    
    setDownloadStarted(true);
    setDownloadError(null);
    
    fetch(downloadUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Server responded with status: ${response.status}`);
        }
        return response.blob();
      })
      .then(blob => {
        // Verify blob is valid video
        if (blob.type && !blob.type.includes('video/')) {
          console.warn('Unexpected content type:', blob.type);
          // Continue anyway as sometimes the type might not be set correctly
        }
        
        // Create a blob URL
        const blobUrl = window.URL.createObjectURL(blob);
        
        // Create a temporary anchor element
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = blobUrl;
        a.download = 'youtube-reel.mp4';
        
        // Trigger download
        document.body.appendChild(a);
        a.click();
        
        // Clean up
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
          document.body.removeChild(a);
          setDownloadStarted(false);
        }, 1000);
      })
      .catch(error => {
        console.error('Download error:', error);
        setDownloadError('Failed to download the video. Please try again.');
        setDownloadStarted(false);
        alert(`Download failed: ${error.message}. Please try again.`);
      });
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <h2 className="text-lg font-semibold mb-3">Output Settings</h2>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Aspect Ratio</label>
          <div className="grid grid-cols-2 gap-2">
            {aspectRatioOptions.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => setAspectRatio(option.value)}
                className={`p-2 border rounded-md text-sm text-left transition ${
                  aspectRatio === option.value
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <span className="flex items-center">
                  <span className="mr-2">{option.icon}</span>
                  <span>{option.value}</span>
                </span>
                <span className="block text-xs text-gray-500 mt-1">{option.label.split('(')[1]?.replace(')', '')}</span>
              </button>
            ))}
          </div>
        </div>
        
        <div className="border-t border-gray-200 pt-3">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center text-sm text-gray-600 hover:text-gray-800 mb-2"
          >
            <Settings size={16} className="mr-1" />
            {showAdvanced ? 'Hide Advanced Settings' : 'Show Advanced Settings'}
          </button>
          
          {showAdvanced && (
            <>
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1">Transition Style</label>
                <select
                  value={transitionType}
                  onChange={(e) => setTransitionType(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-300 focus:outline-none text-sm"
                >
                  <option value="fade">Fade (Smooth transition)</option>
                  <option value="cut">Cut (No transition)</option>
                  <option value="crossfade">Crossfade (Overlapping transition)</option>
                  <option value="wipe">Wipe (Side-to-side transition)</option>
                </select>
              </div>
              
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1">Output Quality</label>
                <select
                  value={quality}
                  onChange={(e) => setQuality(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-300 focus:outline-none text-sm"
                >
                  <option value="high">High (1080p) - Best quality</option>
                  <option value="medium">Medium (720p) - Balanced</option>
                  <option value="low">Low (480p) - Smaller file size</option>
                </select>
              </div>
            </>
          )}
        </div>
        
        <button
          onClick={handleProcessVideo}
          disabled={isProcessing || timeSegments.length === 0}
          className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-md text-white font-medium ${
            isProcessing || timeSegments.length === 0 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-indigo-600 hover:bg-indigo-700'
          } transition duration-200`}
        >
          {isProcessing ? (
            <>
              <div className="flex flex-col items-center w-full">
                {processingError ? (
                  <div className="p-2 mb-2 bg-red-100 border border-red-400 text-red-700 rounded-md w-full">
                    <div className="flex items-center">
                      <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
                      <span className="text-sm">{processingError}</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center mb-2">
                      <svg className="animate-spin h-5 w-5 text-white mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Processing Video...</span>
                    </div>
                    
                    {/* Progress bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                      <div 
                        className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300" 
                        style={{ width: `${processingProgress}%` }}
                      ></div>
                    </div>
                    
                    {/* Progress details */}
                    <div className="text-xs text-white w-full flex justify-between">
                      <span>{processingProgress}%</span>
                      <span>
                        {stageDescriptions[processingStage] || processingStage.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              <Scissors size={20} />
              Create Reel
            </>
          )}
        </button>
        
        {downloadUrl && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-green-700 text-sm mb-2">Your video is ready!</p>
            {downloadError && (
              <div className="p-2 mb-2 bg-red-100 border border-red-400 text-red-700 rounded-md w-full">
                <div className="flex items-center">
                  <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
                  <span className="text-sm">{downloadError}</span>
                </div>
              </div>
            )}
            <a
              href={process.env.NODE_ENV === 'development' ? '#demo-download' : downloadUrl}
              onClick={handleDownload}
              className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-md text-white font-medium ${downloadStarted ? 'bg-gray-500 cursor-wait' : 'bg-green-600 hover:bg-green-700'} transition duration-200`}
              disabled={downloadStarted}
            >
              {downloadStarted ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Downloading...
                </>
              ) : (
                <>
                  <Download size={20} />
                  Download MP4
                </>
              )}
            </a>
            {process.env.NODE_ENV === 'development' && (
              <p className="text-xs text-gray-500 text-center mt-2">
                Note: In development mode, actual file downloads are simulated. In production, this would download your processed video file.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SegmentList;
